export interface ExportOrderItem {
  item_name: string;
  order_qty: number;
}

export interface ExportOrderData {
  id: number;
  partner_name: string;
  created_at: string;
  transport_provider?: string;
  items: ExportOrderItem[];
}

const SCALE = 2;
const WIDTH = 700;
const MARGIN_X = 30;
const CONTENT_WIDTH = WIDTH - MARGIN_X * 2;
const COL_SERIAL_W = 80;
const COL_QTY_W = 120;
const ITEM_COL_MIN_W = 400;
const ITEM_COL_MAX_W = CONTENT_WIDTH - COL_SERIAL_W - COL_QTY_W;
const CELL_PADDING_X = 12;
const ROW_H = 34;
const TABLE_HEADER_H = 42;
const TOP_MARGIN = 30;
const BOTTOM_MARGIN = 30;
const TITLE_H = 34;
const TITLE_GAP = 14;
const INFO_BLOCK_H = 66;
const INFO_GAP = 20;
const DIVIDER_GAP = 20;
const CONT_LABEL_H = 26;
const CONT_LABEL_GAP = 10;
const FIRST_PAGE_ROWS = 20;

const FONT_FAMILY = 'Helvetica, Arial, sans-serif';
const COLOR_TEXT_PRIMARY = '#1a1a1a';
const COLOR_TEXT_SECONDARY = '#757575';
const COLOR_DIVIDER = '#e0e0e0';
const COLOR_TABLE_HEADER_BG = '#f5f5f5';
const COLOR_BORDER = '#e0e0e0';

function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = '...';
  let truncated = text;
  while (truncated.length > 0 && ctx.measureText(truncated + ellipsis).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + ellipsis;
}

function computePageHeaderHeight(isFirstPage: boolean): number {
  if (isFirstPage) {
    return TOP_MARGIN + TITLE_H + TITLE_GAP + INFO_BLOCK_H + INFO_GAP + DIVIDER_GAP;
  }
  return TOP_MARGIN + CONT_LABEL_H + CONT_LABEL_GAP;
}

// Continuation pages reuse page 1's total height budget; since they skip the
// customer/date/transport block, that freed space becomes extra table rows.
function computeContinuationCapacity(): number {
  const page1Height = computePageHeaderHeight(true) + TABLE_HEADER_H + ROW_H * FIRST_PAGE_ROWS + BOTTOM_MARGIN;
  const contHeaderHeight = computePageHeaderHeight(false);
  const capacity = Math.floor((page1Height - contHeaderHeight - TABLE_HEADER_H - BOTTOM_MARGIN) / ROW_H);
  return Math.max(capacity, 1);
}

function paginateItems(items: ExportOrderItem[]): ExportOrderItem[][] {
  if (items.length <= FIRST_PAGE_ROWS) return [items];
  const pages: ExportOrderItem[][] = [items.slice(0, FIRST_PAGE_ROWS)];
  const contCapacity = computeContinuationCapacity();
  let remaining = items.slice(FIRST_PAGE_ROWS);
  while (remaining.length > 0) {
    pages.push(remaining.slice(0, contCapacity));
    remaining = remaining.slice(contCapacity);
  }
  return pages;
}

// Sizes the item name column to the widest name on this page (clamped between
// ITEM_COL_MIN_W and ITEM_COL_MAX_W), so short names don't leave the column
// looking mostly empty. Longer names are truncated with "..." in drawRow.
function computeItemColumnWidth(ctx: CanvasRenderingContext2D, items: ExportOrderItem[]): number {
  ctx.font = `bold 13px ${FONT_FAMILY}`;
  let maxTextWidth = ctx.measureText('Item Name').width;

  ctx.font = `13px ${FONT_FAMILY}`;
  items.forEach(item => {
    const width = ctx.measureText(item.item_name).width;
    if (width > maxTextWidth) maxTextWidth = width;
  });

  const ideal = maxTextWidth + CELL_PADDING_X * 2;
  return Math.min(ITEM_COL_MAX_W, Math.max(ITEM_COL_MIN_W, ideal));
}

function drawTableHeader(ctx: CanvasRenderingContext2D, y: number, tableX: number, itemColW: number): number {
  const tableWidth = COL_SERIAL_W + itemColW + COL_QTY_W;

  ctx.fillStyle = COLOR_TABLE_HEADER_BG;
  ctx.fillRect(tableX, y, tableWidth, TABLE_HEADER_H);
  ctx.strokeStyle = COLOR_BORDER;
  ctx.lineWidth = 1;
  ctx.strokeRect(tableX, y, tableWidth, TABLE_HEADER_H);

  ctx.fillStyle = COLOR_TEXT_PRIMARY;
  ctx.font = `bold 13px ${FONT_FAMILY}`;
  ctx.textBaseline = 'middle';

  const centerY = y + TABLE_HEADER_H / 2;

  ctx.textAlign = 'center';
  ctx.fillText('S.No', tableX + COL_SERIAL_W / 2, centerY);

  ctx.textAlign = 'left';
  ctx.fillText('Item Name', tableX + COL_SERIAL_W + CELL_PADDING_X, centerY);

  ctx.textAlign = 'center';
  ctx.fillText('Quantity', tableX + COL_SERIAL_W + itemColW + COL_QTY_W / 2, centerY);

  return y + TABLE_HEADER_H;
}

function drawRow(
  ctx: CanvasRenderingContext2D,
  y: number,
  serial: number,
  item: ExportOrderItem,
  tableX: number,
  itemColW: number
): number {
  const tableWidth = COL_SERIAL_W + itemColW + COL_QTY_W;

  ctx.strokeStyle = COLOR_BORDER;
  ctx.lineWidth = 1;
  ctx.strokeRect(tableX, y, tableWidth, ROW_H);

  ctx.fillStyle = COLOR_TEXT_PRIMARY;
  ctx.font = `13px ${FONT_FAMILY}`;
  ctx.textBaseline = 'middle';
  const centerY = y + ROW_H / 2;

  ctx.textAlign = 'center';
  ctx.fillText(String(serial), tableX + COL_SERIAL_W / 2, centerY);

  ctx.textAlign = 'left';
  const itemNameX = tableX + COL_SERIAL_W + CELL_PADDING_X;
  const itemNameMaxW = itemColW - CELL_PADDING_X * 2;
  ctx.fillText(fitText(ctx, item.item_name, itemNameMaxW), itemNameX, centerY);

  ctx.textAlign = 'center';
  ctx.fillText(String(item.order_qty), tableX + COL_SERIAL_W + itemColW + COL_QTY_W / 2, centerY);

  return y + ROW_H;
}
function drawFirstPageHeader(ctx: CanvasRenderingContext2D, order: ExportOrderData): number {
  let y = drawOrderId(ctx, order);

  const colW = CONTENT_WIDTH / 3;
  const infoItems: [string, string][] = [
    ['Customer Name', order.partner_name || ''],
    ['Date', new Date(order.created_at).toLocaleDateString()],
    ['Transport Name', order.transport_provider || ''],
  ];

  infoItems.forEach(([label, value], i) => {
    const x = MARGIN_X + colW * i;

    ctx.fillStyle = COLOR_TEXT_SECONDARY;
    ctx.font = `11px ${FONT_FAMILY}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(label.toUpperCase(), x, y);

    ctx.fillStyle = COLOR_TEXT_PRIMARY;
    ctx.font = `bold 15px ${FONT_FAMILY}`;
    ctx.fillText(fitText(ctx, value, colW - 16), x, y + 20);
  });

  y += INFO_BLOCK_H + INFO_GAP;

  ctx.strokeStyle = COLOR_DIVIDER;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(MARGIN_X, y);
  ctx.lineTo(WIDTH - MARGIN_X, y);
  ctx.stroke();
  y += DIVIDER_GAP;

  return y;
}

function drawOrderId(ctx: CanvasRenderingContext2D, order: ExportOrderData): number {
  let y = TOP_MARGIN;
  ctx.fillStyle = COLOR_TEXT_SECONDARY;
  ctx.font = `italic 13px ${FONT_FAMILY}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(`Order #${order.id}`, MARGIN_X, y);
  y += CONT_LABEL_H + CONT_LABEL_GAP;
  return y;
}

function drawPageNumber(ctx: CanvasRenderingContext2D, pageIndex: number, totalPages: number): void {
  if (totalPages <= 1) return;
  ctx.fillStyle = COLOR_TEXT_SECONDARY;
  ctx.font = `11px ${FONT_FAMILY}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.fillText(`Page ${pageIndex + 1} of ${totalPages}`, WIDTH - MARGIN_X, TOP_MARGIN);
}

function renderPageCanvas(
  order: ExportOrderData,
  pageItems: ExportOrderItem[],
  serialOffset: number,
  pageIndex: number,
  totalPages: number
): HTMLCanvasElement {
  const isFirstPage = pageIndex === 0;
  const headerHeight = computePageHeaderHeight(isFirstPage);
  const height = headerHeight + TABLE_HEADER_H + ROW_H * pageItems.length + BOTTOM_MARGIN;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is not available');
  }
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, WIDTH, height);

  let y = isFirstPage ? drawFirstPageHeader(ctx, order) : drawOrderId(ctx, order);
  drawPageNumber(ctx, pageIndex, totalPages);

  const itemColW = computeItemColumnWidth(ctx, pageItems);
  const tableX = MARGIN_X;

  y = drawTableHeader(ctx, y, tableX, itemColW);
  pageItems.forEach((item, i) => {
    y = drawRow(ctx, y, serialOffset + i + 1, item, tableX, itemColW);
  });

  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to generate image blob'));
    }, 'image/png');
  });
}

export async function generateOrderImages(order: ExportOrderData): Promise<Blob[]> {
  const pages = paginateItems(order.items);
  const blobs: Blob[] = [];
  let serialOffset = 0;
  for (let i = 0; i < pages.length; i++) {
    const canvas = renderPageCanvas(order, pages[i], serialOffset, i, pages.length);
    blobs.push(await canvasToBlob(canvas));
    serialOffset += pages[i].length;
  }
  return blobs;
}
